<div class="fixed-center">
  @if (copyAlertMessage) {
    <ngb-alert class="alert-border" type="success" [dismissible]="true" (closed)="closeAlert()">
      {{ copyAlertMessage | messageTranslate }}
    </ngb-alert>
  }
</div>

<div class="container-fluid px-2">
  <!-- First row -->
  <div class="row align-items-center gx-3 my-2"
       (mouseenter)="updateCopyLinkVisibility(true)"
       (mouseleave)="updateCopyLinkVisibility(false)">
    <!-- Logo -->
    <div class="col-auto">
      <div class="d-flex align-items-center justify-content-center logo-container">
        @if (ticket?.assignees?.length) {
          <app-show-user-entity-logo [entity]="ticket?.assignees[0]?.entity"/>
        } @else {
          <app-icon class="logo-placeholder" name="logo-placeholder" />
        }
      </div>
    </div>
    <!-- Summary -->
    <div class="col">
      <div class="row gy-2">
        <div class="col summary">
          @if (ticket.site && showSiteName) {
            <div class="summary-lines-spacer small">
              @if (showSiteNameAsLink) {
                <a #siteBoardLink
                   [routerLink]="['/tickets', getTicketsPathByTicketType(TicketType.ESCALATED), ticket.site.site_id, ticket.ticket_id]"
                   (click)="closeModal()">
                  {{ticket.site.site_name}}</a>
                @if (showCopyLink) {
                  <span class="text-body-secondary"
                        [title]="'Copy link to clipboard' | messageTranslate">
                    <fa-icon icon="link"
                             class="clickable"
                             [fixedWidth]="true"
                             (click)="copyToClipboard(siteBoardLink.href)"/></span>
                }
              } @else {
                <span>{{ticket.site.site_name}}</span>
              }
            </div>
          }
          <div class="fw-bold summary-lines-spacer">
            @if (cardSummaryOnly) {
              <span>{{ summaryParts.firstPart }}</span>
              <span class="d-inline-flex text-nowrap">{{ summaryParts.lastWordPart }}
                <app-icon id="expand-icon"
                          size="lg"
                          display="flex"
                          name="expand"
                          [rotate]="ICON_ROTATE.twoSeventy"/></span>
            } @else {
              {{ ticket.summary }}
            }
          </div>
          <div class="row gx-1" [ngClass]="{'text-secondary': cardSummaryOnly}">
            @if (shouldDisplayTicketStatus()) {
              <div class="col-auto">
                <span>{{ ticket?.status?.display_text }}</span>
                @if (shouldDisplayTicketAssignee()) {
                  <span> -> </span>
                }
              </div>
            }
            @if (shouldDisplayTicketAssignee()) {
              <div class="col-auto">
                @if (!readOnly && assigneeEditable && !assigneesLoading) {
                  <div class="row gx-0 align-items-center">
                    <div class="col">
                      @if (isMobile) {
                        <select class="w-100"
                                [formControl]="assigneeControl"
                                (blur)="setAssigneeEditable(false)">
                          <option [ngValue]="unassignedOption.id">{{ unassignedOption.fullName }}</option>
                          @for (entity of boardAssigneeGroups; track entity.entity.id) {
                            <optgroup [label]="entity.entity.name">
                              @for (user of entity.users; track user.id) {
                                <option [ngValue]="user.id">{{ user.fullName }}</option>
                              }
                            </optgroup>
                          }
                        </select>
                      } @else {
                        <app-ace-select #aceSelectComponent
                                        bindLabel="fullName"
                                        bindValue="id"
                                        [formControl]="assigneeControl"
                                        [clearable]="false"
                                        [groupBy]="groupByFn"
                                        [multiSelect]="false"
                                        [items]="assigneeOptions"
                                        (touched)="setAssigneeEditable(false)"/>
                      }
                    </div>
                    <div class="col-auto">
                      <ng-template [ngTemplateOutlet]="assigneeEditActionIcon"
                                   [ngTemplateOutletContext]="{icon: 'times', spinnerRequired: isMobile}"/>
                    </div>
                  </div>
                } @else {
                  <div [ngClass]="{'clickable': !readOnly && canUpdateTicketAssignee()}"
                       [attr.title]="isCurrentAssigneeDeleted ? ('User with id does not exist' | messageTranslate:{ id: ticketAssignee.id }) : ''"
                       (click)="!readOnly ? assigneeClicked() : undefined">
                    <span>{{ fullNameParts?.firstPart }}</span>
                    <span class="d-inline-flex text-nowrap">{{ fullNameParts?.lastWordPart }}
                      <ng-template [ngTemplateOutlet]="assigneeEditActionIcon"
                                   [ngTemplateOutletContext]="{icon: 'pencil-alt', spinnerRequired: true}"/></span>
                  </div>
                }
              </div>
            }
            @if (!cardSummaryOnly) {
              <div class="col-auto">
                <ng-template [ngTemplateOutlet]="escalatedTicketsCount"
                             [ngTemplateOutletContext]="{badge: false, showLinks: true}"/>
              </div>
            }
          </div>
        </div>
        <!-- Approve / Updated -->
        <div class="col-md-auto d-flex gap-2">
          @if (!readOnly && !cardSummaryOnly) {
            @if ((isActionAvailable(ExecutableAction.APPROVE) && isActionAvailable(ExecutableAction.RESOLVED)) || isActionAvailable(ExecutableAction.ON_HOLD) || isActionAvailable(ExecutableAction.NOT_DOING)) {
              <div ngbDropdown
                    container="body"
                    class="d-inline-block align-self-center"
                    autoClose="outside">
                <button type="button"
                        class="btn btn-secondary d-flex align-items-center"
                        id="moreActions"
                        ngbDropdownToggle>
                  <span class="me-1">{{'More' | messageTranslate}}</span>
                </button>
                <div ngbDropdownMenu aria-labelledby="moreActions">
                  @if (isActionAvailable(ExecutableAction.APPROVE)) {
                    <ng-template [ngTemplateOutlet]="moreActionButtonTemplate"
                                 [ngTemplateOutletContext]="{buttonAction: ExecutableAction.RESOLVED, text: {messageKey: 'Resolve'}}" />
                  }
                  <ng-template [ngTemplateOutlet]="moreActionButtonTemplate"
                               [ngTemplateOutletContext]="{buttonAction: ExecutableAction.ON_HOLD, text: {messageKey: 'On Hold'}}" />
                  <ng-template [ngTemplateOutlet]="moreActionButtonTemplate"
                               [ngTemplateOutletContext]="{buttonAction: ExecutableAction.NOT_DOING, text: {messageKey: 'Not Doing'}}" />
                </div>
              </div>
            }
          }
          @if (!readOnly && isActionAvailable(ExecutableAction.APPROVE)) {
            <app-button class="align-self-center"
                        visual="primary"
                        label="Approve"
                        (buttonClick)="onClickApprove($event)"/>
          } @else {
            @if (!readOnly && !cardSummaryOnly && isActionAvailable(ExecutableAction.RESOLVED)) {
              <app-button class="align-self-center"
                          visual="success"
                          label="Resolve"
                          [loading]="isActionExecuting(ExecutableAction.RESOLVED)"
                          [disabled]="isActionInProgress()"
                          (buttonClick)="updateStatus(ExecutableAction.RESOLVED, $event)"/>
            }
            <!-- applying .summary class to force a line height on underlying text to be the same as ticket summary -->
            @if (cardSummaryOnly) {
              <div class="summary text-secondary">
                {{ updatedOrResolved() | messageTranslate }}
                <app-time-lapsed [timestamp]="ticket.updated_at" />
              </div>
            }
          }
        </div>
      </div>
      <ng-template #assigneeEditActionIcon let-spinner="spinnerRequired" let-icon="icon">
        <!-- px-0 is added for the 'times' icon on mobile as it is smaller than the 'pencil' and so appeared too far from the standard select -->
        @if (!readOnly && canUpdateTicketAssignee()) {
          <fa-icon class="text-body-secondary clickable small"
                   [ngClass]="(icon === 'times' && isMobile ? 'px-0': 'px-1')"
                   [icon]="icon"
                   [fixedWidth]="true"
                   [appShowFaSpinner]="spinner && (assigneeSaving || (assigneeEditable && assigneesLoading))"
                   (click)="toggleAssigneeEditable()"/>
        }
      </ng-template>
    </div>
  </div>

  <!-- comment row -->
  @if ((ticket?.status?.status_id !== TicketStatusId.STATUS_NOT_DOING) && showFirstComment && ticket?.comments?.[0]; as comment) {
    <div class="row mt-3 mb-2">
      <div class="col-12">
        <div class="bg-light rounded p-2">
          <div class="m-1">
            <app-ellipsis [text]="comment.text | linkify : {usersByEmail: assigneesByEmail, previewMode: true}" [canToggle]="false">
              <span ngProjectAs="[postContent]" class="ms-1 text-nowrap text-secondary">&ndash; {{comment.author_full_name ?? ''}}</span>
            </app-ellipsis>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Second row -->
   @if (cardSummaryOnly
        && (ticket?.status?.status_id !== TicketStatusId.STATUS_NOT_DOING)
        && (benefitName != null
          || showPriority(ticket?.priority?.priority_id)
          || canShowOtherEscalatedTicketsCount
          || getActionLinkedAlerts(ticket)?.length)) {
    <div class="border-bottom my-3"></div>
    <div class="row lh-1 gx-1 pb-2">
      @if (getActionLinkedAlerts(ticket); as linkedAlerts) {
        @if (linkedAlerts.length) {
          <div class="col-auto">
            <app-escalated-ticket-alert-status [alertTickets]="linkedAlerts" />
          </div>
        }
      }
      @if (showPriority(ticket?.priority?.priority_id)) {
        <div class="col-auto">
          <app-priority-badge [priority]="ticket.priority" />
        </div>
      }
      @if (benefitName) {
        <div class="col-auto">
          <span class="badge border-radius-sm d-flex text-bg-info-gray">
            {{ benefitName }}
          </span>
        </div>
      }
      <div class="col-auto">
        <ng-template [ngTemplateOutlet]="escalatedTicketsCount"
                     [ngTemplateOutletContext]="{badge: true, showLinks: false}"/>
      </div>
    </div>
  }
</div>

<ng-template #escalatedTicketsCount let-badge="badge" let-showLinks="showLinks" >
  @if (canShowOtherEscalatedTicketsCount) {
    <app-other-escalated-tickets-count [ticket]="ticket"
                                       [count]="otherEscalatedTicketsCount"
                                       [showLink]="showLinks"
                                       [showFullMessage]="showLinks"
                                       [badge]="badge" />
  }
</ng-template>

<ng-template #moreActionButtonTemplate let-buttonAction="buttonAction" let-text="text">
  @if (!readOnly && isActionAvailable(buttonAction)) {
    <button type="button"
            class="dropdown-item"
            ngbDropdownItem
            [disabled]="isActionInProgress()"
            (click)="updateStatus(buttonAction, $event)">
      @if (isActionExecuting(buttonAction)) {
        <fa-icon icon="spinner" animation="spin" [fixedWidth]="true" />
      }
      {{text | messageTranslate}}
    </button>
  }
</ng-template>
